(ns hai.tasks.mini-app
  "The tasks mini-app card shared by the home dashboard and the extension
   sidebar: the mini-app frame with the perforation shifted right and the
   tasks-tinted header bar, the list's lanes, and the two views the card swaps
   its list for —
   Add a task, and a task open in it. Hosts bring the card frame, the sizes,
   and the data wiring; everything a user sees comes from here. Mirrors
   [[hai.notes.mini-app]]."
  (:require
   [hai.entity.kebab :as kebab]
   [hai.mini-apps.frame :as frame]
   [hai.sharing.helper :as sharing]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.tasks.drawer.core :as drawer-core]
   [hai.tasks.drawer.data :as drawer-data]
   [hai.tasks.drawer.share-bar :as share-bar]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.sync-mark :as sync-mark]
   [hai.tasks.ui :as ui]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e!]]
   [lambdaisland.ornament :as o]))

(o/defstyled card frame/card
  [:&::before {:left "auto" :right "40px"}]
  [header/bar
   {:border-top-left-radius "16px"
    :border-top-right-radius "16px"}]
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "16px"
     :border-bottom-right-radius "16px"}]]
  ;; Rows float as inset cards rather than a bordered list. The gap separates
  ;; them now that every card is bordered. The inset holds them off the lane
  ;; headers above and below and the card's sides.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "9px"
    :padding "9px"}])

;; The column the lanes stack in: the frame's scrolling list body, flush, since
;; the headers and rows carry their own spacing. No bottom padding: a sticky
;; header's bottom offset counts from inside it, so a row would peek out under
;; the lowest header. It fills the clip around it, which the host sizes.
(o/defstyled sections frame/list-body
  {:padding "0" :gap "0"})

;;
;; Lanes
;;

(defn- lane
  [{:keys [label tooltip tasks open? index lane-count on-toggle row-opts]}]
  [:<>
   [frame/lane-header {:label      label
                       :tooltip    tooltip
                       :item-count (count tasks)
                       :index      index
                       :lane-count lane-count
                       :open?      open?
                       :on-toggle  on-toggle}]
   (when open?
     (into [:div.section-tasks]
           (map (fn [task]
                  ^{:key (:task/uuid task)}
                  [task-card/task-row-component task row-opts]))
           tasks))])

(defn lanes
  "The list's lanes, None to Later, each a [[frame/lane-header]] over its rows
   while open; a lane with no tasks isn't drawn. Headers and rows go straight
   into the host's scroll column, with no box per lane, since a sticky header
   only sticks inside its parent. `buckets` maps a lane id to its tasks,
   `open` is the set of open lane ids, `on-toggle` gets the id pressed, and
   `row-opts` go to every row ([[hai.tasks.card/task-row-component]])."
  [{:keys [buckets open on-toggle row-opts]}]
  (let [drawn (filterv #(seq (get buckets (:id %))) data/bucket-tabs)]
    (into [:<>]
          (map-indexed (fn [index {:keys [id label tooltip]}]
                         ^{:key id}
                         [lane {:label label
                                :tooltip tooltip
                                :tasks (get buckets id)
                                :open? (contains? open id)
                                :index index
                                :lane-count (count drawn)
                                :on-toggle #(on-toggle id)
                                :row-opts row-opts}]))
          drawn)))

;;
;; Add a task, and a task open in the card
;;

;; What the card holds under the header in place of the list: the same clip
;; the list sits in, so the card keeps its height across the switch. The form
;; is exactly as tall as it, and gives room up inside; the pane scrolls only
;; for a card too short for the form's title, field and tab. Standard
;; scrollbar props only — see [[frame/list-body]].
(o/defstyled editor-pane :div
  {:flex "1 1 auto" :min-height "0"
   :display "flex" :flex-direction "column"
   :overflow-y "auto"
   :overflow-x "hidden"
   :scrollbar-width "thin"
   :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"}
  [form/shell {:flex "1 1 0" :min-height "0"}])

(defn- assignee-picker-slot [{:keys [on-pick trigger]}]
  [assignee-picker/component
   {:editable? true
    :on-pick on-pick
    :label "Assign"
    :trigger trigger}])

(def ^:private share-icon (icons/mui-icon :outlined :person_add))

;; A disabled button swallows hover, so its tooltip is on the slot around it
;; and the button steps aside for the pointer, as the note's share button does.
(o/defstyled share-slot :span
  {:display "inline-flex"}
  [:svg {:width "18px" :height "18px"}]
  [:button:disabled {:opacity "0.4" :cursor "default" :background "transparent"
                     :pointer-events "none"}])

(defn share-button
  "The header's Share, as a note's editor has it: on the open drawer on
   `drawer-key` it turns the drawer to Share's search and gives it the caret;
   on the shut drawer it brings the search out, or puts it away, asking first
   while anyone picked hasn't been added ([[hai.tasks.drawer]]). Disabled,
   saying why, for anyone who may not share the task. It carries its drawer's
   key, for the focus to come back to it."
  [{:keys [drawer-key can-share?]}]
  (let [drawer @(<s [::drawer-data/drawer drawer-key])
        ;; At once, so the search is there for the caret on the next render.
        press! (fn [^js e]
                 (let [root (.getRootNode (.-currentTarget e))
                       focus? (drawer-core/focuses-field? drawer :share)]
                   (>e! [::drawer-data/press-add drawer-key :share])
                   (when focus?
                     (share-bar/focus-field! root drawer-key))))]
    [tt/tooltip {:content (if can-share?
                            "Share this task"
                            "Only the owner or an admin can share this task")}
     ;; A plain :span carrying the styled class by name, not `share-slot`
     ;; itself — an Ornament component doesn't forward refs, which Radix's
     ;; trigger composition needs, and the tooltip silently never opens (as in
     ;; `hai.entity.sections`).
     [:span {:class (str share-slot)}
      [:button.bar-action-btn
       {:type "button"
        :aria-label "Share this task"
        :data-drawer-share (pr-str drawer-key)
        :disabled (not can-share?)
        :on-click press!}
       [share-icon]]]]))

(defn- composer-layout
  "A new task over the card: the header retitled, carrying its Share, Cancel
   and Save, over the form's pieces."
  [form]
  [:<>
   [header/bar {:title "Add a task"}
    [share-button {:drawer-key (drawer-core/drawer-key nil (:draft-id form))
                   :can-share? true}]
    [form/header-actions form]]
   ;; The deadline sheet covers the whole card.
   [frame/list-clip {:class "card-wide-overlays"}
    [editor-pane
     [form/card-layout form]]]])

(defn composer-view
  "Add-a-task mode: the header retitled, with Share, Cancel and Save in it,
   over the composer on the host's `draft-id`. `on-save` gets the staged
   fields — the host creates the task and closes the composer once it lands;
   `on-close` is Cancel, which closes the composer at once with nothing staged
   and asks first otherwise. `saving?` is the host's create in flight; `show-hint?`
   shows the keyboard shortcut on Save (off for touch)."
  [{:keys [draft-id saving? show-hint? on-save on-close]}]
  [form/composer
   {:draft-id draft-id
    :default-urgency @(<s [::data/default-urgency])
    :saving? saving?
    :show-hint? show-hint?
    :on-save on-save
    :on-cancel on-close
    :render-assignee-picker assignee-picker-slot}
   composer-layout])

(defn open-task-body
  "An open task's body, under its header: the composer on `task`, which saves
   as it is edited, or the same layout read-only for a requester who may not
   edit it; either way the stamp ends the chip row, pressable by the owner and
   the assignee. `change-fx` rides every save's success and `stamp-fx` the
   stamp's; `spaces?` and `on-open-entity` as the form has them. The Tasks
   card's ([[editing-view]]) and the web-app's task modal."
  [{:keys [task spaces? on-open-entity change-fx stamp-fx]}]
  (let [task-uuid (:task/uuid task)
        done? (some? (:task/completed-at task))
        current-user @(<s [:auth/identity])]
    ;; The deadline sheet covers the whole card.
    [frame/list-clip {:class "card-wide-overlays"}
     [editor-pane
      [form/card-form
       {:editing? (sharing/can-edit? task)
        :task task
        :spaces? spaces?
        :on-open-entity on-open-entity
        :done? done?
        :change-fx change-fx
        :stamp [ui/stampbox
                {:done?      done?
                 :task-title (:task/title task)
                 :disabled?  (not (data/can-complete? current-user task))
                 ;; Re-completing re-stamps: a new completed-at remounts the
                 ;; stamp so its jitter is rolled again.
                 :stamp-key  (:task/completed-at task)
                 :on-toggle  #(>e [(if done? ::data/uncomplete ::data/complete) task-uuid
                                   stamp-fx])}]}]]]))

(defn editing-view
  "A task open over the card: \"← Back to tasks\" in the header, its Share,
   and the ⋮ holding Delete — disabled, saying why, for anyone but the task's owner —
   over the composer on `task`, which saves as it is edited, or the same
   layout read-only for a requester who may not edit it; either way the stamp
   ends the chip row, pressable by the owner and the assignee. Back and Delete
   put the list back.

   Surface differences are passed in: `spaces?` (adding and removing spaces),
   `on-open-entity` (routes a linked chip's click), `change-fx` (rides every
   save's, the stamp's and the delete's success) and `on-add`, which starts a
   new task from the ＋ in the header."
  [{:keys [task spaces? on-open-entity change-fx on-add]}]
  (let [task-uuid (:task/uuid task)
        close! #(>e [::data/close-editor])
        ;; The slice reloads after each save, the way the board re-resolves
        ;; its rows: a tag typed here is patched in without a uuid, and only
        ;; server truth can give the next click something to diff against.
        ;; The save mark's Refresh fetches with it too.
        reload-fx (into [[:dispatch [::data/load-owned-or-assigned-tasks]]] change-fx)]
    [:<>
     [header/bar {:on-back close!}
      (when on-add
        [frame/action-button {:label "New task"
                              :icon [icons/outlined-add {:size 18}]
                              :on-click on-add}])
      (when (sharing/can-edit? task)
        [sync-mark/mark {:task task :class "bar-action-btn" :change-fx reload-fx}])
      [share-button {:drawer-key (drawer-core/drawer-key task-uuid nil)
                     :can-share? (sharing/can-share? task)}]
      [kebab/menu
       {:copy-url (kebab/app-url "tasks" task-uuid)
        :on-delete (fn []
                     (close!)
                     (>e [::data/delete task-uuid change-fx]))
        :delete-label "Delete task"
        :confirm-prompt "Delete this task?"
        :delete-disabled-reason (when-not (sharing/owner? task)
                                  "Only the owner can delete this task")
        :aria-label "Task actions"}]]
     [open-task-body {:task task
                      :spaces? spaces?
                      :on-open-entity on-open-entity
                      :change-fx reload-fx
                      :stamp-fx change-fx}]]))
